import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Row, Tab, Tabs } from "react-bootstrap";
import { push } from "store/actions/router";
import { map } from "lodash";

import IconSpinner from "components/IconSpinner";
import EmployerInfo from "./EmployerInfo";
import DisabledCompanyBanner from "./DisabledCompanyBanner";
import FrozenCompanyBanner from "./FrozenCompanyBanner";
import CrmLink from "./CrmLink";
import Groups from "./Groups";
import RosterUploads from "./RosterUploads";
// ../KYB/Company's own-props don't include `canEdit`, but this call site passes
// it (inert extra prop carried over from the original JS, ignored by Company) —
// cast the default import to a component type that accepts it at the boundary.
import CompanyJS from "../KYB/Company";
import Controller from "../KYB/Controller";
import Summary from "../KYB/Summary";
import Payrolls from "./Payrolls";
import { getCompany } from "services/adminService";
import {
  EmployerApplicationRequiresAdditionalDocumentation,
  EmployerApplicationSubmittedKYB,
  employerOnboardingStates,
  EmployerPendingKYBSubmission,
  employerYellowPathStates,
} from "statics/states";
import MemberDocs from "./MemberDocs";
import Billing from "./Billing";
import Organization from "pages/dashboards/adminDashboard/employers/Organization";
import EntityLimits from "../EntityLimits";
import { isSuperIconAdmin } from "store/selectors/admin";
import MissedPayrolls from "./MissedPayrolls";
import EntityPayrollDetails from "pages/dashboards/adminDashboard/EntityPayrollDetails";
import EmployerContributionAbility from "pages/dashboards/adminDashboard/EmployerContributionAbility";
import type { RootState } from "types/state";
import type { CompanyByIdQuery } from "types/graphql";
import type { Company as CompanyModel } from "types/models";

// Loosely typed at the boundary so the inert `canEdit` prop (not in Company's
// own-props) typechecks; see the import comment above.
const Company = CompanyJS as React.ComponentType<{
  canEdit: boolean;
  companyId?: string;
  onBack: () => void;
}>;

type CompanyEntity = NonNullable<CompanyByIdQuery["companyById"]>;

const mapStateToProps = (state: RootState) => {
  return {
    isSuperAdmin: isSuperIconAdmin(state),
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// Own props injected by the Employer_WithLocation wrapper below.
interface OwnProps {
  routeName: string;
  companyId?: string;
  // present in original PropTypes but never read or provided here
  error?: string;
  isFetching?: boolean;
}

type Props = WithApolloClient<PropsFromRedux & OwnProps>;

interface State {
  tab: string;
  initialFetching: boolean;
  companyState: string | null;
  // Drives the frozen banner; kept in sync with EmployerInfo's Freeze/Unfreeze
  // button via _onFreezeStatusChange.
  freezeStatus: string | null;
  companyAdminEmail?: string;
  company?: CompanyEntity;
  // read in render() (this.state.fetchingAdmins) but never written — always undefined
  fetchingAdmins?: boolean;
}

class Employer extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      tab: this._calcActiveTab(props.routeName),
      initialFetching: true,
      companyState: "",
      freezeStatus: null,
    };
  }

  async componentDidMount() {
    const company = await getCompany(
      this.props.client as ApolloClient<object>,
      // companyId comes from the /employers/:id route param — always present here
      this.props.companyId as string
    );
    const {
      currentState,
      user: { email },
    } = company;

    this.setState({
      initialFetching: false,
      companyState: currentState,
      freezeStatus: company.freezeDetails?.status ?? null,
      companyAdminEmail: email,
      company,
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.routeName !== this.props.routeName) {
      this.setState({ tab: this._calcActiveTab(this.props.routeName) });
    }
  }

  _onCompanyUpdate = (params: Partial<CompanyEntity> = {}) => {
    this.setState({
      company: { ...(this.state.company as CompanyEntity), ...params },
    });
  };

  _onFreezeStatusChange = (freezeStatus: string | null) => {
    this.setState({ freezeStatus });
  };

  _onCompanyStateChange = (companyState: string) => {
    this.setState({ companyState });
  };

  _getTabs = () => {
    let allTabs = [
      {
        label: "Info",
        id: "info",
      },
      {
        label: "Groups/Banks",
        id: "groups",
      },
      {
        label: "Payrolls",
        id: "payrolls",
      },
      {
        label: "Missed Payrolls",
        id: "missed_payrolls",
      },
      {
        label: "Organization",
        id: "organization",
      },
      {
        label: "Roster Uploads",
        id: "roster",
      },
      {
        label: "Crm",
        id: "crm",
      },
      {
        label: "KYB",
        id: "kyb/summary",
      },
    ];

    if (
      this.state.companyState &&
      !employerOnboardingStates.includes(this.state.companyState)
    ) {
      allTabs.push({
        label: "Billing",
        id: "billing",
      });
    }

    if (this.props.isSuperAdmin) {
      allTabs.push({
        label: "Limits",
        id: "limits",
      });
      allTabs.splice(
        allTabs.findIndex((tab) => tab.id === "payrolls") + 1,
        0,
        {
          label: "ACH Details",
          id: "ach",
        },
        {
          label: "Contribution Ability",
          id: "contribution_ability",
        }
      );
    }

    if (
      // companyState is `string | null`; null never matches, same as before
      employerYellowPathStates.includes(this.state.companyState as string)
    ) {
      allTabs.push({
        label: "Yellowpath",
        id: "documents",
      });
    }

    return allTabs;
  };

  _tabUpdate(value: { k: string | null }) {
    this.setState({ tab: value.k as string });
    this.props.push(`/employers/${this.props.companyId}/${value.k}`);
  }

  _controlledTabs() {
    const tabs = this._getTabs();
    return (
      <Tabs
        activeKey={this.state.tab}
        id="tab-company"
        onSelect={(k) => this._tabUpdate({ k })}
      >
        {map(tabs, (tab) => {
          return <Tab key={tab.id} eventKey={tab.id} title={tab.label} />;
        })}
      </Tabs>
    );
  }

  _calcActiveTab = (routeName: string) => {
    switch (routeName) {
      case `/employers/${this.props.companyId}/info`:
        return "info";
      case `/employers/${this.props.companyId}/kyb/info`:
      case `/employers/${this.props.companyId}/kyb/owners`:
      case `/employers/${this.props.companyId}/kyb/summary`:
        return "kyb/summary";
      case `/employers/${this.props.companyId}/documents`:
        return "documents";
      case `/employers/${this.props.companyId}/groups`:
        return "groups";
      case `/employers/${this.props.companyId}/payrolls`:
        return "payrolls";
      case `/employers/${this.props.companyId}/roster`:
        return "roster";
      case `/employers/${this.props.companyId}/billing`:
        return "billing";
      case `/employers/${this.props.companyId}/organization`:
        return "organization";
      case `/employers/${this.props.companyId}/limits`:
        return "limits";
      case `/employers/${this.props.companyId}/crm`:
        return "crm";
      case `/employers/${this.props.companyId}/missed_payrolls`:
        return "missed_payrolls";
      case `/employers/${this.props.companyId}/ach`:
        return "ach";
      case `/employers/${this.props.companyId}/contribution_ability`:
        return "contribution_ability";
      default:
        return "info";
    }
  };

  _calcKybContent = () => {
    const allowKybEdit =
      // companyState is `string | null`; null never matches, same as before
      employerOnboardingStates.includes(this.state.companyState as string) &&
      this.state.companyState !== EmployerApplicationSubmittedKYB;

    const allowKybSubmission =
      this.state.companyState === EmployerPendingKYBSubmission ||
      this.state.companyState ===
        EmployerApplicationRequiresAdditionalDocumentation;

    switch (this.props.routeName) {
      case `/employers/${this.props.companyId}/kyb/info`:
        return (
          <Company
            canEdit={allowKybEdit}
            companyId={this.props.companyId}
            onBack={() => {
              this.props.push(`/employers/${this.props.companyId}/kyb/summary`);
            }}
          />
        );
      case `/employers/${this.props.companyId}/kyb/owners`:
        return (
          // Controller requires companyId: string; route guarantees it
          <Controller
            // @ts-expect-error Controller declares only companyId/memberId; canEdit is an inert pre-existing extra prop the component ignores (passed in original JS, never read by Controller)
            canEdit={allowKybEdit}
            companyId={this.props.companyId as string}
          />
        );
      case `/employers/${this.props.companyId}/kyb/summary`:
        return (
          <Summary
            canEdit={allowKybEdit}
            canSubmit={allowKybSubmission}
            companyId={this.props.companyId}
          />
        );
      case `/employers/${this.props.companyId}/limits`:
        return (
          <Summary
            canEdit={allowKybEdit}
            canSubmit={allowKybSubmission}
            companyId={this.props.companyId}
          />
        );
    }
  };

  _getCardContent = () => {
    switch (this.state.tab) {
      case "info":
        return (
          <EmployerInfo
            companyId={this.props.companyId}
            onFreezeStatusChange={this._onFreezeStatusChange}
            onCompanyStateChange={this._onCompanyStateChange}
          />
        );
      case "kyb/summary":
        return this._calcKybContent();
      case "documents":
        // companyId from /employers/:id route — always present; MemberDocs requires string
        return <MemberDocs companyId={this.props.companyId as string} />;
      case "groups":
        return (
          <Groups
            companyId={this.props.companyId}
            // Groups types `company` as the full schema entity (types/models Company);
            // this.state.company is the narrower CompanyById query subset. Boundary cast
            // passes the exact same (possibly-undefined) runtime value through unchanged.
            company={this.state.company as unknown as CompanyModel}
          />
        );
      case "payrolls":
        return <Payrolls companyId={this.props.companyId} />;
      case "missed_payrolls":
        return <MissedPayrolls companyId={this.props.companyId} />;
      case "roster":
        return <RosterUploads companyId={this.props.companyId} />;
      case "organization":
        return <Organization companyId={this.props.companyId} />;
      case "billing":
        return (
          <Billing
            companyId={this.props.companyId}
            // company is set in componentDidMount before this tab can render;
            // billingType is `string | null` on the model — null may flow at runtime as before
            billingType={this.state.company!.billingType as string | undefined}
            companyAdminEmail={this.state.companyAdminEmail}
            updateCompany={this._onCompanyUpdate}
          />
        );
      case "limits":
        return (
          <EntityLimits entityType="EMPLOYER" entityId={this.props.companyId} />
        );
      case "crm":
        return <CrmLink companyId={this.props.companyId} />;
      case "ach":
        return <EntityPayrollDetails companyId={this.props.companyId} />;
      case "contribution_ability":
        return <EmployerContributionAbility companyId={this.props.companyId} />;
    }
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }
    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Employer Info</h1>

            <div className="main-content">
              {this.state.fetchingAdmins && <IconSpinner centered />}
              <Row>
                <Col>
                  <DisabledCompanyBanner
                    companyState={this.state.companyState}
                  />
                  <FrozenCompanyBanner freezeStatus={this.state.freezeStatus} />
                  <div className="card-tab">{this._controlledTabs()}</div>
                  <Card>
                    <Card.Body>{this._getCardContent()}</Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

const Employer_WithLocation = (props: WithApolloClient<PropsFromRedux>) => {
  const { pathname: routeName } = useLocation();
  const { id: companyId } = useParams();
  return <Employer {...props} routeName={routeName} companyId={companyId} />;
};

export default connector(
  withApollo<WithApolloClient<PropsFromRedux>>(Employer_WithLocation)
);
